/**
 * Auto-Add Product v6.2.0 (Production)
 *
 * Manual add, auto-remove free gift system with AJAX section rendering.
 * - User clicks "Add Free Gift" button when threshold met
 * - Gift auto-removed when cart drops below threshold
 * - Uses Shopify Bundled Section Rendering API for smooth updates
 */

const AutoAddProduct = (function () {
  'use strict';

  const config = window.themeAutoAddProduct || {};
  const rules = config.rules || [];
  const debugEnabled = config.debugLogging || false;

  // Sections to update via Bundled Section Rendering
  const SECTIONS_DRAWER = ['cart-drawer', 'cart-icon-bubble'];
  const SECTIONS_PAGE = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];

  // Get appropriate sections based on context
  const getSections = () => isCartPage() ? SECTIONS_PAGE : SECTIONS_DRAWER;

  // Check if on cart page
  const isCartPage = () => document.body.classList.contains('template-cart');

  // Cache for last known cart state (for predictive calculations)
  let cachedCart = null;

  // Flag to prevent infinite loops
  let isProcessing = false;

  // Debug helper - only logs when debug mode enabled in theme settings
  const log = (...args) => { if (debugEnabled) console.log('[AutoAddProduct]', ...args); };

  // Debug function for manual console inspection
  const debug = () => {
    console.group('[AutoAddProduct] Debug Info v6.2.0');
    console.log('Config:', config);
    console.log('Enabled:', config.enabled);
    console.log('Rules:', rules);
    console.log('Cached Cart:', cachedCart);
    console.log('Is Cart Page:', isCartPage());
    if (rules.length > 0) {
      console.log('Primary rule:', rules[0]);
      console.log('Threshold (rupees):', rules[0].threshold);
      console.log('Min Threshold (rupees):', rules[0].minThreshold || 0);
      console.log('Variant ID:', rules[0].variantId);
    }
    console.groupEnd();
    return { config, rules, enabled: config.enabled, cachedCart };
  };

  // Early returns for misconfigured feature
  if (!config.enabled) {
    return {
      init: () => { },
      addGift: () => Promise.resolve({ success: false, error: 'Feature disabled' }),
      debug,
      version: '6.2.0'
    };
  }

  if (rules.length === 0) {
    return {
      init: () => { },
      addGift: () => Promise.resolve({ success: false, error: 'No rules configured' }),
      debug,
      version: '6.2.0'
    };
  }

  const rule = rules[0];
  const threshold = rule.threshold * 100; // Convert to cents/paise
  const minThreshold = (rule.minThreshold || 0) * 100; // Convert to cents/paise, default to 0

  if (!rule.variantId) {
    return {
      init: () => { },
      addGift: () => Promise.resolve({ success: false, error: 'Missing variantId' }),
      debug,
      version: '6.2.0'
    };
  }

  // ============ HELPERS ============

  const fetchCart = () => fetch('/cart.js').then(r => r.json());

  // free_gift_added / free_gift_removed / free_gift_complete were removed per request.
  // The trackEvent helper and the lastTrackedState flag existed only to emit them, so both
  // are gone too. The free-gift feature itself is untouched - only its analytics.
  /**
   * DATA VALIDATION: Validates cart data from external sources
   * Strictly checks that total_price is a valid number - rejects strings to force API fallback
   */
  const validateCartData = (cart, source = 'unknown') => {
    if (!cart || typeof cart !== 'object' || !Array.isArray(cart.items)) {
      return null;
    }

    const tp = cart.total_price;
    if (typeof tp === 'number' && !isNaN(tp) && isFinite(tp)) {
      return cart;
    }

    log('Rejected', source, 'data: invalid total_price');
    return null;
  };

  /**
   * Smart cart resolver - uses cached/event data when available
   * Eliminates unnecessary /cart.js API calls
   */
  const resolveCart = async (eventData) => {
    // Priority 1: Cart data from PubSub event
    if (eventData?.cartData) {
      const validated = validateCartData(eventData.cartData, 'PubSub');
      if (validated) {
        cachedCart = validated;
        return cachedCart;
      }
    }

    // Priority 2: Cart data nested in detail (custom events)
    if (eventData?.detail?.cartData) {
      const validated = validateCartData(eventData.detail.cartData, 'CustomEvent');
      if (validated) {
        cachedCart = validated;
        return cachedCart;
      }
    }

    // Priority 3: Direct cart object passed
    if (eventData?.items) {
      const validated = validateCartData(eventData, 'Direct');
      if (validated) {
        cachedCart = validated;
        return cachedCart;
      }
    }

    // Priority 4: Fallback to API call
    cachedCart = await fetchCart();
    return cachedCart;
  };

  const findGift = (cart) => {
    if (!cart || !cart.items) return null;
    return cart.items.find(item =>
      item.properties && item.properties._auto_gift === rule.id
    );
  };

  const findGiftByVariantId = (cart) => {
    if (!cart || !cart.items) return null;
    return cart.items.find(item =>
      String(item.variant_id) === String(rule.variantId)
    );
  };

  const getState = (cart) => {
    if (!cart) return 'locked';
    const meetsThreshold = cart.total_price >= threshold;
    const hasGift = !!findGift(cart);
    if (!meetsThreshold) return 'locked';
    return hasGift ? 'complete' : 'unlocked';
  };

  /**
   * Check if widget should be visible based on minimum threshold
   */
  const shouldShowWidget = (cart) => {
    if (!cart) return false;
    return cart.total_price >= minThreshold;
  };

  const getProgress = (cart) => {
    const totalPrice = typeof cart.total_price === 'number' ? cart.total_price : 0;
    const percent = Math.min(100, Math.round((totalPrice / threshold) * 100));
    const remaining = Math.max(0, Math.ceil((threshold - totalPrice) / 100));
    return { percent, remaining };
  };

  // ============ SECTION RENDERING ============

  /**
   * Render sections from Shopify's bundled section rendering response
   */
  const renderSections = (response) => {
    if (!response || !response.sections) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }

    // 1. Cart Drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      try {
        cartDrawer.renderContents(response);
      } catch (e) { /* silent */ }
    }

    // 2. Cart Icon Bubble
    if (response.sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        try {
          const doc = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html');
          const newContent = doc.querySelector('.shopify-section');
          if (newContent) bubble.innerHTML = newContent.innerHTML;
        } catch (e) { /* silent */ }
      }
    }

    // 3. Main Cart Items (cart page only)
    if (response.sections['main-cart-items']) {
      const mainItems = document.getElementById('main-cart-items');
      if (mainItems) {
        try {
          const doc = new DOMParser().parseFromString(response.sections['main-cart-items'], 'text/html');
          const newContent = doc.querySelector('.js-contents') || doc.querySelector('cart-items');
          const target = mainItems.querySelector('.js-contents') || mainItems.querySelector('cart-items');
          if (newContent && target) {
            target.innerHTML = newContent.innerHTML;
          } else {
            const newSection = doc.querySelector('.shopify-section');
            if (newSection) mainItems.innerHTML = newSection.innerHTML;
          }
        } catch (e) { /* silent */ }
      }
    }

    // 4. Main Cart Footer (cart page only)
    if (response.sections['main-cart-footer']) {
      const mainFooter = document.getElementById('main-cart-footer');
      if (mainFooter) {
        try {
          const doc = new DOMParser().parseFromString(response.sections['main-cart-footer'], 'text/html');
          const newContent = doc.querySelector('.js-contents');
          const target = mainFooter.querySelector('.js-contents');
          if (newContent && target) {
            target.innerHTML = newContent.innerHTML;
          } else {
            const newSection = doc.querySelector('.shopify-section');
            if (newSection) mainFooter.innerHTML = newSection.innerHTML;
          }
        } catch (e) { /* silent */ }
      }
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: response }));
  };

  // ============ CART OPERATIONS ============

  /**
   * Add gift to cart
   */
  const addGift = async (ruleId) => {
    const targetRule = rules.find(r => r.id === ruleId) || rule;

    if (!targetRule || !targetRule.variantId) {
      return { success: false, error: 'Invalid rule configuration' };
    }

    try {
      const cart = await resolveCart();
      const cartTotal = cart.total_price;
      const requiredTotal = targetRule.threshold * 100;

      if (cartTotal < requiredTotal) {
        return { success: false, error: `Threshold not met. Need ₹${(requiredTotal - cartTotal) / 100} more.` };
      }

      if (findGift(cart)) {
        return { success: false, error: 'Gift already in cart' };
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: targetRule.variantId,
          quantity: targetRule.quantity || 1,
          properties: { _auto_gift: targetRule.id, _VariantTitle: targetRule.productName || '' },
          sections: getSections()
        })
      }).then(r => r.json());

      if (response.status === 422 || response.message) {
        return { success: false, error: response.description || response.message || 'Failed to add gift' };
      }

      // Cart page: reload for full consistency
      if (isCartPage()) {
        window.location.reload();
        return { success: true, response };
      }

      // Cart drawer: render sections via AJAX
      renderSections(response);

      // Update cache and widgets
      cachedCart = await fetchCart();
      updateWidgets(cachedCart);

      // Publish event for other listeners
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-add-product', cartData: cachedCart });
      }
      
      return { success: true, response };

    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Remove gift from cart (standard method)
   */
  const removeGift = async (cart) => {
    const gift = findGift(cart) || findGiftByVariantId(cart);
    if (!gift) return { success: false };

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: gift.key,
          quantity: 0,
          sections: getSections()
        })
      }).then(r => r.json());


      // Cart page: reload for full consistency
      if (isCartPage()) {
        window.location.reload();
        return { success: true };
      }

      // Cart drawer: render sections via AJAX
      renderSections(response);

      // Update cache and widgets
      cachedCart = await fetchCart();
      updateWidgets(cachedCart);

      // Publish event
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-add-product', cartData: cachedCart });
      }

      

      return { success: true };

    } catch (error) {
      window.location.reload();
      return { success: false, error: error.message };
    }
  };

  // ============ PREDICTIVE BATCH UPDATE ============

  /**
   * Batch update - remove gift AND update item in single API call
   */
  const batchUpdateWithGiftRemoval = async (itemKey, newQuantity, giftKey) => {
    try {
      const updates = {};
      updates[itemKey] = newQuantity;
      updates[giftKey] = 0;

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          sections: getSections()
        })
      }).then(r => r.json());

      // Cart page: reload for consistency
      if (isCartPage()) {
        window.location.reload();
        return { success: true, response };
      }

      // Cart drawer: render sections
      renderSections(response);

      // Update cache
      cachedCart = response;
      updateWidgets(cachedCart);

      // Publish event
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-add-product-batch', cartData: cachedCart });
      }

      return { success: true, response };

    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Calculate if threshold will drop below limit after a quantity change
   */
  const willDropBelowThreshold = (cart, itemIndex, newQuantity) => {
    if (!cart || !cart.items || !cart.items[itemIndex]) return false;

    const item = cart.items[itemIndex];
    const currentQty = item.quantity;
    const qtyDelta = currentQty - newQuantity;

    if (qtyDelta <= 0) return false;

    const itemUnitPrice = item.final_line_price / item.quantity;
    const priceReduction = itemUnitPrice * qtyDelta;
    const projectedTotal = cart.total_price - priceReduction;

    return projectedTotal < threshold;
  };

  // ============ UI UPDATE ============

  const updateWidgets = (cart) => {
    if (!cart) return;

    const state = getState(cart);
    const { percent, remaining } = getProgress(cart);
    const isVisible = shouldShowWidget(cart);


    document.querySelectorAll('[data-milestone-widget]').forEach(widget => {
      widget.dataset.visible = isVisible ? 'true' : 'false';
      widget.dataset.state = state;

      const fill = widget.querySelector('[data-progress-fill]');
      if (fill) fill.style.width = `${percent}%`;

      const remainingEl = widget.querySelector('[data-remaining]');
      if (remainingEl) remainingEl.textContent = remaining;

      const icon = widget.querySelector('[data-gift-icon]');
      if (icon) icon.classList.toggle('active', state !== 'locked');

      const btn = widget.querySelector('[data-milestone-add-btn]');
      if (btn && state === 'unlocked') {
        btn.disabled = false;
        const textEl = btn.querySelector('.milestone-add-btn__text');
        const loadingEl = btn.querySelector('.milestone-add-btn__loading');
        if (textEl) textEl.classList.remove('hidden');
        if (loadingEl) loadingEl.classList.add('hidden');
      }
    });
  };

  // ============ MAIN PROCESS ============

  const process = async (eventData) => {
    if (isProcessing) return;

    const source = eventData?.source || eventData?.detail?.source;
    if (source === 'auto-add-product' || source === 'auto-add-product-batch') return;

    isProcessing = true;

    try {
      const cart = await resolveCart(eventData);

      // Auto-remove if threshold not met but gift exists
      if (cart.total_price < threshold && findGift(cart)) {
        await removeGift(cart);
        return;
      }

      updateWidgets(cart);
    } catch (error) {
      log('Process error:', error);
    } finally {
      isProcessing = false;
    }
  };

  // ============ PREDICTIVE REMOVAL INTERCEPTOR ============

  const installPredictiveRemoval = () => {
    const checkAndInstall = () => {
      const CartItemsClass = customElements.get('cart-items');
      if (!CartItemsClass) {
        setTimeout(checkAndInstall, 100);
        return;
      }

      if (CartItemsClass.prototype._autoAddPatched) return;

      const originalUpdateQuantity = CartItemsClass.prototype.updateQuantity;
      if (!originalUpdateQuantity) return;

      CartItemsClass.prototype.updateQuantity = function (line, quantity, event, name, variantId) {
        const cart = cachedCart;

        if (cart && findGift(cart)) {
          const itemIndex = line - 1;
          const item = cart.items[itemIndex];

          if (item && willDropBelowThreshold(cart, itemIndex, quantity)) {
            const gift = findGift(cart);

            this.enableLoading(line);

            batchUpdateWithGiftRemoval(item.key, quantity, gift.key)
              .then(result => {
                if (!result.success) {
                  originalUpdateQuantity.call(this, line, quantity, event, name, variantId);
                } else {
                  this.disableLoading(line);
                }
              })
              .catch(() => {
                originalUpdateQuantity.call(this, line, quantity, event, name, variantId);
              });

            return;
          }
        }

        originalUpdateQuantity.call(this, line, quantity, event, name, variantId);
      };

      CartItemsClass.prototype._autoAddPatched = true;
    };

    checkAndInstall();
  };

  // ============ INIT ============

  const init = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (eventData) => process(eventData));
    }

    document.addEventListener('cart:updated', (event) => process(event));
    document.addEventListener('cart:refresh', (event) => process(event));

    installPredictiveRemoval();

    fetchCart().then(cart => {
      cachedCart = cart;
      updateWidgets(cart);
    });
  };

  return {
    init,
    addGift,
    removeGift,
    process,
    debug,
    batchUpdateWithGiftRemoval,
    willDropBelowThreshold,
    getCachedCart: () => cachedCart,
    getState: async () => getState(await resolveCart()),
    version: '6.2.0'
  };
})();

// Auto-init
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', AutoAddProduct.init);
} else {
  AutoAddProduct.init();
}

window.AutoAddProduct = AutoAddProduct;

// MilestoneWidget for onclick handlers
window.MilestoneWidget = window.MilestoneWidget || {
  addGift: async function (ruleId, callerBtn) {
    const btn = callerBtn || document.querySelector('[data-milestone-add-btn]');
    if (!btn || btn.disabled) return;

    btn.disabled = true;
    const textEl = btn.querySelector('.milestone-add-btn__text');
    const loadingEl = btn.querySelector('.milestone-add-btn__loading');
    if (textEl) textEl.classList.add('hidden');
    if (loadingEl) loadingEl.classList.remove('hidden');

    if (window.AutoAddProduct?.addGift) {
      const result = await window.AutoAddProduct.addGift(ruleId);
      if (!result.success) {
        btn.disabled = false;
        if (textEl) textEl.classList.remove('hidden');
        if (loadingEl) loadingEl.classList.add('hidden');
      }
    }
  },

  copyCoupon: function (code, btn) {
    navigator.clipboard.writeText(code).then(function () {
      const toast = btn.closest('.milestone-text')?.querySelector('.milestone-copied-toast');
      if (toast) {
        toast.classList.add('show');
        setTimeout(() => toast.classList.remove('show'), 1500);
      }
    }).catch(() => { /* silent */ });
  }
};
